import dayjs from 'dayjs'
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { payAndMakeEventBooking } from 'src/consumer/components/EventBooking/BookingHelperFunctions'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { PublicAppFooter } from 'src/consumer/components/PublicAppFooter'
import PublicAppHeader from 'src/consumer/components/PublicAppHeader'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { appVersion, getCountryCode, platformWeb } from 'src/consumer/services'
import { currency_list } from 'src/consumer/constants/currencyList'
import {
  getDataWithoutParams,
  getPublicDataWithoutParams,
  postDataWithoutParams,
} from 'src/consumer/services/featureServices'
import { getImageLink, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { saveClinicWalkinBookingHandoff } from 'src/shared/clinicWalkinBookingHandoff'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'
import { CPBPOMBookAndPayButton, CPBPOMContainer } from './ClinicBrandPageStyles'

const ClubbedAuthModal = React.lazy(() =>
  import('src/consumer/components/SignIn/ClubbedAuthComponents').then((module) => ({
    default: module.ClubbedAuthModal,
  })),
)

const WALKIN_BOOKING_SOURCE = 'clinic_walkin_qr'
const CODE_EXPIRED = 49081
const LAST_PATIENT_PROFILE_KEY = 'patientPortal.currentProfileId'
const WALKIN_FEE_CURRENCY_PREFIX = {
  inr: 'INR ',
  usd: '$',
  eur: 'EUR ',
  gbp: 'GBP ',
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

function unwrapResponse(response) {
  if (response?.response) {
    return { status: response.response.status, data: response.response.data }
  }
  return response
}

function currencyPrefix(...values) {
  const code = String(firstPresent(...values) || '').toLowerCase()
  const fromList = currency_list?.find((item) => item?.code === code)?.symbol
  return firstPresent(fromList, WALKIN_FEE_CURRENCY_PREFIX[code], code ? `${code.toUpperCase()} ` : '')
}

function formatAmount(value) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return String(value)
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: Number.isInteger(numericValue) ? 0 : 2,
  }).format(numericValue)
}

function formatFeeValue(value, currencyId, currencySymbol) {
  if (value === undefined || value === null || value === '') return ''
  const prefix = firstPresent(currencySymbol, currencyPrefix(currencyId), '')
  return `${prefix}${formatAmount(value)}`
}

function formatWalkinEventFee(event) {
  const pricing = event?.pricing || {}
  const fee = firstPresent(
    event?.discountedFee,
    event?.fee,
    event?.finalPrice,
    event?.discountedPrice,
    event?.introductoryPrice,
    event?.price,
    pricing?.discountedFee,
    pricing?.fee,
    pricing?.finalPrice,
    pricing?.discountedPrice,
    pricing?.introductoryPrice,
    pricing?.price,
  )
  return formatFeeValue(
    fee,
    firstPresent(event?.currencyId, event?.currency, pricing?.currencyId, pricing?.currency, event?.defaultCurrency),
    firstPresent(event?.currencySymbol, pricing?.currencySymbol),
  )
}

function formatPlanFee(plan) {
  const currencyId = firstPresent(plan?.currency, plan?.currencyId, plan?.defaultCurrency)
  const symbol = firstPresent(plan?.currencySymbol, currencyPrefix(currencyId))
  const returningFee = firstPresent(plan?.discountedPrice, plan?.finalPrice)
  const firstVisitFee = firstPresent(plan?.introductoryPrice)

  if (
    returningFee !== undefined &&
    returningFee !== null &&
    returningFee !== '' &&
    firstVisitFee !== undefined &&
    firstVisitFee !== null &&
    firstVisitFee !== '' &&
    String(returningFee) !== String(firstVisitFee)
  ) {
    return `Returning ${formatFeeValue(returningFee, currencyId, symbol)} / first visit ${formatFeeValue(
      firstVisitFee,
      currencyId,
      symbol,
    )}`
  }

  return formatFeeValue(
    firstPresent(plan?.discountedPrice, plan?.finalPrice, plan?.introductoryPrice, plan?.price),
    currencyId,
    symbol,
  )
}

function eventDurationMinutes(eventDetail, referenceSlot) {
  const configuredDuration = Number(eventDetail?.durationInMins)
  if (Number.isFinite(configuredDuration) && configuredDuration > 0) return configuredDuration
  if (referenceSlot?.startTime && referenceSlot?.endTime) {
    const slotDuration = dayjs(referenceSlot.endTime).diff(dayjs(referenceSlot.startTime), 'minute')
    if (slotDuration > 0) return slotDuration
  }
  return 15
}

function getWalkinBookableSlot(todaysSlots, eventDetail) {
  const slots = (Array.isArray(todaysSlots) ? todaysSlots : [])
    .filter((slot) => slot?.startTime && slot?.endTime)
    .sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf())
  if (slots.length === 0) return null

  const firstSlot = slots[0]
  const now = dayjs()
  if (dayjs(firstSlot.startTime).valueOf() > now.valueOf()) {
    return { ...firstSlot, immediate: false }
  }

  const start = now.second(0).millisecond(0)
  const duration = eventDurationMinutes(eventDetail, firstSlot)
  return {
    startTime: start.format('YYYY-MM-DDTHH:mm:ssZ'),
    endTime: start.add(duration, 'minute').format('YYYY-MM-DDTHH:mm:ssZ'),
    immediate: true,
  }
}

function profileDisplayName(profile) {
  if (!profile) return 'Patient'
  const rawName = (profile.displayName || profile.name || '').trim()
  const baseName = rawName.split(/\s(?:-|\u2014)\s/)[0]?.trim()
  if (profile.relation === 'SELF') {
    return profile.brandName ? `You - ${profile.brandName}` : 'You'
  }
  return baseName || profile.relation || 'Patient'
}

function writeLastPatientProfile(profileId) {
  try {
    if (profileId) localStorage.setItem(LAST_PATIENT_PROFILE_KEY, profileId)
  } catch {
    // no-op
  }
}

export default function ClinicWalkinPage() {
  const navigate = useNavigate()
  const { token } = useParams()
  const { profileData, profileDataLoading, refreshProfileData } = useContext(ProfileContext)

  const [pageState, setPageState] = useState('resolving')
  const [resolveError, setResolveError] = useState('')
  const [resolveData, setResolveData] = useState(null)
  const [eventsById, setEventsById] = useState({})
  const [selectedEventId, setSelectedEventId] = useState(null)
  const [feeByEventId, setFeeByEventId] = useState({})
  const [nextSlotsLoading, setNextSlotsLoading] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [bookingError, setBookingError] = useState(null)
  const [chosenSlot, setChosenSlot] = useState(null)
  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [initiatedBookingData, setInitiatedBookingData] = useState()
  const [bookedEventData, setBookedEventData] = useState()
  const [patientProfiles, setPatientProfiles] = useState([])
  const [patientProfilesLoading, setPatientProfilesLoading] = useState(false)
  const [patientProfilesError, setPatientProfilesError] = useState('')
  const [selectedPatientId, setSelectedPatientId] = useState('')
  const pollRef = useRef(null)

  const loggedIn = !isTokenInvalidOrAbsent(localStorage.getItem('token'))

  const fetchEventBookingPage = useCallback(async (data, eventId) => {
    const from = dayjs().format('YYYY-MM-DD')
    const to = dayjs().endOf('month').format('YYYY-MM-DD')
    const timeZone = encodeURIComponent(getTimezoneOffset())
    let query =
      `mentorSlug=${encodeURIComponent(data.mentorSlug)}` +
      `&eventId=${encodeURIComponent(eventId)}` +
      `&from=${from}&to=${to}&timeZone=${timeZone}`
    if (data.clinicLocationId) query += `&clinicId=${encodeURIComponent(data.clinicLocationId)}`

    const response = unwrapResponse(
      await getPublicDataWithoutParams(`/api/public/clinic-booking-page?${query}`),
    )
    if (response?.status !== 200) return null
    const bookingPage = (response?.data?.data ?? response?.data)?.bookingPage
    if (!bookingPage?.event) return null

    const eventDetail = {
      ...bookingPage.event,
      contextId: bookingPage.event.contextId || data.clinicLocationId,
      clinicBrandId: bookingPage.event.clinicBrandId || data.clinicBrandId,
      bookingSource: WALKIN_BOOKING_SOURCE,
    }
    const slots = Array.isArray(bookingPage.availableSlots)
      ? bookingPage.availableSlots.map(({ date, slots: s }) => ({
          date,
          availableTimeSlots: Array.isArray(s) ? s : [],
        }))
      : []

    return {
      eventId,
      title: bookingPage.event.title || 'Session',
      eventDetail,
      feeLabel: formatWalkinEventFee(eventDetail),
      slots,
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function resolve() {
      setPageState('resolving')
      setResolveError('')
      setEventsById({})
      setSelectedEventId(null)

      const response = await postDataWithoutParams('/api/public/clinic-walkin/resolve', { token })
      if (cancelled) return

      const status = response?.status ?? response?.response?.status
      const body = response?.data ?? response?.response?.data
      if (status !== 200 || !isCommonResponseSuccessful(body?.code)) {
        if (body?.code === CODE_EXPIRED) {
          setResolveError(
            body?.message || 'This QR has expired. Please scan the latest QR shown at the clinic.',
          )
          setPageState('expired')
        } else {
          setResolveError(
            body?.message || 'This booking link is not valid. Please scan the QR at the clinic again.',
          )
          setPageState('invalid')
        }
        return
      }

      const data = body?.data ?? body
      setResolveData(data)
      const ids = [data?.defaultEventId, ...(data?.specialEventIds || [])].filter(Boolean)
      const primaryEventId = data?.defaultEventId || ids[0]
      const backgroundIds = ids.filter((id) => id !== primaryEventId)
      const primaryItem = primaryEventId ? await fetchEventBookingPage(data, primaryEventId) : null
      if (cancelled) return

      if (primaryItem) {
        setEventsById({ [primaryItem.eventId]: primaryItem })
        setSelectedEventId(primaryItem.eventId)
        setPageState('booking')
        if (backgroundIds.length > 0) {
          Promise.all(backgroundIds.map((eventId) => fetchEventBookingPage(data, eventId))).then((items) => {
            if (cancelled) return
            setEventsById((prev) => {
              const next = { ...prev }
              items.filter(Boolean).forEach((item) => {
                next[item.eventId] = item
              })
              return next
            })
          })
        }
        return
      }

      const fallbackItems = await Promise.all(backgroundIds.map((eventId) => fetchEventBookingPage(data, eventId)))
      if (cancelled) return
      const firstFallback = fallbackItems.find(Boolean)
      if (firstFallback) {
        const map = {}
        fallbackItems.filter(Boolean).forEach((item) => {
          map[item.eventId] = item
        })
        setEventsById(map)
        setSelectedEventId(firstFallback.eventId)
        setPageState('booking')
      } else {
        setResolveError('This booking link has no active appointment types. Please ask the clinic desk.')
        setPageState('invalid')
      }
    }

    if (token) resolve()
    return () => {
      cancelled = true
    }
  }, [fetchEventBookingPage, token])

  useEffect(() => {
    if (pageState !== 'booking' || !resolveData || !selectedEventId) return undefined

    async function refresh() {
      setNextSlotsLoading(true)
      try {
        const item = await fetchEventBookingPage(resolveData, selectedEventId)
        if (item) {
          setEventsById((prev) => ({ ...prev, [selectedEventId]: item }))
        }
      } finally {
        setNextSlotsLoading(false)
      }
    }

    pollRef.current = setInterval(refresh, 20000)
    return () => clearInterval(pollRef.current)
  }, [fetchEventBookingPage, pageState, resolveData, selectedEventId])

  const selectedEvent = selectedEventId ? eventsById[selectedEventId] : null
  const todayKey = dayjs().format('YYYY-MM-DD')
  const bookableSlot = useMemo(() => {
    const todaysSlots =
      selectedEvent?.slots?.find((item) => item?.date === todayKey)?.availableTimeSlots || []
    return getWalkinBookableSlot(todaysSlots, selectedEvent?.eventDetail)
  }, [selectedEvent?.eventDetail, selectedEvent?.slots, todayKey])
  const selectedFeeLabel = selectedEvent?.feeLabel || feeByEventId[selectedEventId] || ''
  const selectedPatient = patientProfiles.find((profile) => profile?.id === selectedPatientId)

  useEffect(() => {
    let cancelled = false

    async function fetchFee() {
      if (!selectedEvent?.eventDetail || selectedEvent?.feeLabel || feeByEventId[selectedEventId] !== undefined) {
        return
      }
      const planId = selectedEvent.eventDetail.subscriptionPlanId
      if (!planId) {
        setFeeByEventId((prev) => ({ ...prev, [selectedEventId]: '' }))
        return
      }

      const response = await getDataWithoutParams(
        `/api/subscription-plan?id=${planId}&zoneOffset=${encodeURIComponent(
          getTimezoneOffset(),
        )}&appVersion=${appVersion}&platformType=${platformWeb}&languageCode=${sessionStorage.getItem(
          'languageCode',
        )}&platform=${platformWeb}&countryCode=${getCountryCode()}`,
      )
      if (cancelled) return
      if (response?.status === 200 && response?.data) {
        setFeeByEventId((prev) => ({
          ...prev,
          [selectedEventId]: formatPlanFee(response?.data?.data ?? response?.data),
        }))
      } else {
        setFeeByEventId((prev) => ({ ...prev, [selectedEventId]: '' }))
      }
    }

    fetchFee()
    return () => {
      cancelled = true
    }
  }, [feeByEventId, selectedEvent, selectedEventId])

  const loadPatientProfiles = useCallback(async () => {
    if (!loggedIn) return
    setPatientProfilesLoading(true)
    setPatientProfilesError('')
    try {
      const { listMyProfiles } = await import('src/patient/services/profilesService')
      const profiles = await listMyProfiles()
      const list = Array.isArray(profiles) ? profiles : []
      const clinicBrandId = resolveData?.clinicBrandId
      setPatientProfiles(list)
      setSelectedPatientId((current) => {
        const preferred =
          list.find((profile) => profile?.id === current) ||
          list.find((profile) => clinicBrandId && profile?.brandId === clinicBrandId) ||
          list.find((profile) => profile?.relation === 'SELF') ||
          list[0]
        if (preferred?.id) {
          writeLastPatientProfile(preferred.id)
          return preferred.id
        }
        return current || ''
      })
    } catch (error) {
      setPatientProfiles([])
      setPatientProfilesError('Could not load patient profiles. Booking can still continue.')
    } finally {
      setPatientProfilesLoading(false)
    }
  }, [loggedIn, resolveData?.clinicBrandId])

  useEffect(() => {
    if (pageState === 'booking' && loggedIn) {
      loadPatientProfiles()
    }
  }, [loadPatientProfiles, loggedIn, pageState])

  async function proceedWithBooking(profileToUse) {
    if (!bookableSlot?.startTime || !bookableSlot?.endTime) {
      setBookingError('No bookable slot is available right now. Please ask the clinic desk.')
      return
    }
    if (!selectedEvent?.eventDetail) {
      setBookingError('Could not load this session. Please try again.')
      return
    }
    if (patientProfiles.length > 0 && !selectedPatientId) {
      setBookingError('Please choose the patient for this appointment.')
      return
    }

    const patientReference = selectedPatient
      ? {
          patientId: selectedPatient.id,
          patientProfileId: selectedPatient.id,
          patientName: profileDisplayName(selectedPatient),
          patientNumber: selectedPatient.patientNumber || null,
          relation: selectedPatient.relation || null,
          clinicBrandId: selectedPatient.brandId || resolveData?.clinicBrandId || null,
        }
      : null

    setBookingError(null)
    setChosenSlot(bookableSlot)
    setPaymentLoading(true)
    await payAndMakeEventBooking(
      selectedEvent.eventDetail,
      {
        data: { startTime: bookableSlot.startTime, endTime: bookableSlot.endTime },
        postBufferMins: 0,
        preBufferMins: 0,
      },
      'razorpay',
      'GFT-default',
      null,
      resolveData?.mentorId,
      profileToUse,
      () => null,
      (data) => {
        if (data) setInitiatedBookingData(data)
        setPageState('confirmed')
      },
      'uid',
      'bookNextAvailable',
      (data) => setInitiatedBookingData(data),
      patientReference,
      patientReference ? { patientId: patientReference.patientId } : {},
    )
    setPaymentLoading(false)
  }

  async function handleBookButtonClick() {
    if (isTokenInvalidOrAbsent(localStorage.getItem('token'))) {
      setShowClubbedAuthModal(true)
      return
    }
    if (profileDataLoading || !profileData) {
      setBookingError('Just a moment, loading your account details.')
      if (typeof refreshProfileData === 'function') {
        refreshProfileData({ _forceRefresh: true })
      }
      return
    }
    await proceedWithBooking(profileData)
  }

  async function handleAuthenticated(freshProfile) {
    try {
      sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
      sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
      sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
      localStorage.removeItem('lastChosenFlow')
    } catch {
      // private-browsing: handled downstream
    }
    setShowClubbedAuthModal(false)
    try {
      if (typeof refreshProfileData === 'function') {
        refreshProfileData({ _forceRefresh: true })
      }
    } catch (e) {
      console.warn('Walk-in: refreshProfileData failed (non-fatal):', e?.message)
    }
    await loadPatientProfiles()
    await proceedWithBooking(freshProfile || profileData)
  }

  useEffect(() => {
    if (pageState !== 'confirmed' || bookedEventData) return undefined
    const bookedEventId = initiatedBookingData?.bookedEvent?.id
    if (!bookedEventId) return undefined

    let cancelled = false
    async function getAndSetBookedEventData() {
      for (let attempt = 0; attempt < 5; attempt++) {
        const res = await getDataWithoutParams(
          `/api/secure/user/booked-slot/details?eventId=${bookedEventId}`,
        )
        if (cancelled) return
        if (res?.status === 200 && isCommonResponseSuccessful(res?.data?.code)) {
          const fetched = res?.data?.data?.bookedEvent
          setBookedEventData(fetched)
          if (fetched?.appointmentNumber) return
        }
        await new Promise((r) => setTimeout(r, 1500))
      }
    }

    getAndSetBookedEventData()
    return () => {
      cancelled = true
    }
  }, [bookedEventData, initiatedBookingData?.bookedEvent?.id, pageState])

  useEffect(() => {
    if (pageState !== 'confirmed') return undefined
    const event = bookedEventData || initiatedBookingData?.bookedEvent
    const appointmentTime = event?.startTime || chosenSlot?.startTime
    saveClinicWalkinBookingHandoff({
      eventId: event?.id || initiatedBookingData?.bookedEvent?.id || null,
      appointmentNumber: bookedEventData?.appointmentNumber || event?.appointmentNumber || null,
      title: selectedEvent?.title || 'Session',
      practitionerName: resolveData?.mentorName || '',
      clinicName: resolveData?.clinicName || '',
      scheduledAt: appointmentTime || null,
      durationMinutes: eventDurationMinutes(selectedEvent?.eventDetail, chosenSlot),
      patientProfileId: selectedPatientId || null,
      patientName: selectedPatient ? profileDisplayName(selectedPatient) : profileData?.name || null,
      locationName: resolveData?.clinicName || '',
      sessionMode: 'OFFLINE',
    })

    const timer = setTimeout(() => {
      navigate('/patient-home')
    }, 2600)
    return () => clearTimeout(timer)
  }, [
    bookedEventData,
    chosenSlot,
    initiatedBookingData,
    navigate,
    pageState,
    profileData?.name,
    resolveData?.clinicName,
    resolveData?.mentorName,
    selectedEvent,
    selectedPatient,
    selectedPatientId,
  ])

  function resetForNextPatient() {
    setBookedEventData(undefined)
    setInitiatedBookingData(undefined)
    setChosenSlot(null)
    setBookingError(null)
    setSelectedEventId(resolveData?.defaultEventId)
    setPageState('booking')
  }

  const eventOptions = resolveData
    ? [resolveData.defaultEventId, ...(resolveData.specialEventIds || [])].filter(Boolean)
    : []

  return (
    <>
      <PublicAppHeader config={{ showLinks: false }} />
      {showClubbedAuthModal && (
        <Suspense fallback={<LoadingSpinner bgColor="#F5F5F599" dark />}>
          <ClubbedAuthModal
            open={showClubbedAuthModal}
            onClose={() => setShowClubbedAuthModal(false)}
            loginVariant={false}
            nameFieldHeading="Patient Name"
            preferredLoginInputType="mobile"
            preferredCountryCode="in"
            compactAuthMode
            allowMobileRegistration
            onAuthenticated={handleAuthenticated}
          />
        </Suspense>
      )}

      {pageState === 'resolving' && <LoadingSpinner bgColor="#F5F5F599" dark />}

      {(pageState === 'invalid' || pageState === 'expired') && (
        <CPBPOMContainer>
          <div style={{ fontWeight: 700, marginTop: 40, textAlign: 'center' }}>
            {pageState === 'expired' ? 'QR expired' : 'Invalid link'}
          </div>
          <div style={{ marginTop: 10, textAlign: 'center', maxWidth: 320, fontSize: 14 }}>
            {resolveError}
          </div>
        </CPBPOMContainer>
      )}

      {pageState === 'booking' && resolveData && (
        <CPBPOMContainer>
          <div style={{ width: '100%', maxWidth: 420, padding: '8px 0 28px' }}>
            <div style={{ color: '#6b7280', fontSize: 13, textAlign: 'center' }}>
              {resolveData.clinicName}
            </div>

            <section
              style={{
                marginTop: 12,
                padding: 16,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: '#ffffff',
              }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {resolveData.mentorProfilePicId && (
                  <img
                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                    src={getImageLink(resolveData.mentorProfilePicId)}
                    alt="Practitioner"
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>Booking with</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
                    {resolveData.mentorName}
                  </div>
                  <div style={{ fontSize: 14, color: '#374151', marginTop: 4 }}>
                    {selectedEvent?.title || 'Session'}
                  </div>
                </div>
              </div>

              {selectedFeeLabel && (
                <div
                  style={{
                    marginTop: 12,
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: '#f3f4f6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    fontSize: 14,
                  }}
                >
                  <span style={{ color: '#6b7280' }}>Fee</span>
                  <strong style={{ color: '#111827', textAlign: 'right' }}>{selectedFeeLabel}</strong>
                </div>
              )}
            </section>

            {eventOptions.length > 1 && (
              <div style={{ marginTop: 14 }}>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 6 }}>
                  Session type
                </label>
                <select
                  value={selectedEventId || ''}
                  onChange={(e) => {
                    setBookingError(null)
                    setSelectedEventId(e.target.value)
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    background: '#fff',
                  }}
                >
                  {eventOptions.map((id) => (
                    <option key={id} value={id}>
                      {eventsById[id]?.title || 'Session'}
                      {id === resolveData.defaultEventId ? '' : ' (special)'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <section
              style={{
                marginTop: 14,
                padding: 16,
                borderRadius: 8,
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                textAlign: 'center',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 18 }}>{dayjs().format('DD-MMM-YYYY')}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Today</div>
              <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
                Next available time {nextSlotsLoading && <Spinner animation="border" variant="dark" size="sm" />}
              </div>
              {bookableSlot ? (
                <div style={{ fontSize: 30, fontWeight: 800, color: '#111827', marginTop: 2 }}>
                  {bookableSlot.immediate ? 'Now' : dayjs(bookableSlot.startTime).format('h:mm A')}
                  {bookableSlot.immediate && (
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#6b7280' }}>
                      {dayjs(bookableSlot.startTime).format('h:mm A')}
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 13, marginTop: 8 }}>
                  {nextSlotsLoading ? (
                    <Spinner animation="border" variant="dark" size="sm" />
                  ) : (
                    'No slots available right now'
                  )}
                </div>
              )}
            </section>

            {loggedIn && (
              <section
                style={{
                  marginTop: 14,
                  padding: 16,
                  borderRadius: 8,
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                  Patient for this appointment
                </div>
                {patientProfilesLoading ? (
                  <Spinner animation="border" variant="dark" size="sm" />
                ) : patientProfiles.length > 0 ? (
                  <>
                    <select
                      value={selectedPatientId}
                      onChange={(e) => {
                        setSelectedPatientId(e.target.value)
                        writeLastPatientProfile(e.target.value)
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '1px solid #d1d5db',
                        background: '#fff',
                      }}
                    >
                      {patientProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profileDisplayName(profile)}
                          {profile.patientNumber ? ` - #${profile.patientNumber}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => navigate('/patient-home/profile/family')}
                      style={{
                        marginTop: 10,
                        padding: 0,
                        border: 0,
                        background: 'transparent',
                        color: '#514387',
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                    >
                      Add new patient
                    </button>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    {patientProfilesError || `Continuing as ${profileData?.name || 'this account'}.`}
                  </div>
                )}
              </section>
            )}

            <CPBPOMBookAndPayButton
              style={{ marginTop: 16, width: '100%' }}
              onClick={handleBookButtonClick}
              disabled={paymentLoading || !bookableSlot || (loggedIn && profileDataLoading)}
            >
              {loggedIn ? 'Pay and Book' : 'Continue with mobile and book'}
              {paymentLoading && <Spinner animation="border" variant="light" size="sm" />}
            </CPBPOMBookAndPayButton>

            {bookingError && (
              <div style={{ color: '#c0392b', fontSize: 13, marginTop: 10, textAlign: 'center' }}>
                {bookingError}
              </div>
            )}

            <div style={{ marginTop: 18, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
              Appointments may be delayed due to existing appointments or emergencies.
            </div>
          </div>
        </CPBPOMContainer>
      )}

      {pageState === 'confirmed' && (
        <CPBPOMContainer>
          <div style={{ width: '100%', maxWidth: 420, padding: '32px 0', textAlign: 'center' }}>
            <div
              aria-hidden="true"
              style={{
                width: 64,
                height: 64,
                margin: '0 auto 16px',
                borderRadius: '50%',
                background: '#dcfce7',
                color: '#166534',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 34,
                fontWeight: 800,
              }}
            >
              ✓
            </div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>Booking successful</div>
            <div style={{ marginTop: 8, fontSize: 14, color: '#4b5563' }}>
              Your appointment has been confirmed. We are opening your patient portal now.
            </div>
            <section
              style={{
                marginTop: 18,
                padding: 16,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: '#fff',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 700 }}>{selectedEvent?.title || 'Session'}</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                {resolveData?.mentorName}
                {chosenSlot?.startTime ? ` - ${dayjs(chosenSlot.startTime).format('h:mm A')}` : ''}
              </div>
              {bookedEventData?.appointmentNumber && (
                <div style={{ marginTop: 14, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Appointment number</div>
                  <div style={{ fontSize: 44, fontWeight: 900, color: '#111827' }}>
                    {bookedEventData.appointmentNumber}
                  </div>
                </div>
              )}
            </section>
            <CPBPOMBookAndPayButton
              style={{ marginTop: 18, width: '100%' }}
              onClick={() => navigate('/patient-home')}
            >
              Open patient portal
            </CPBPOMBookAndPayButton>
            <button
              type="button"
              onClick={resetForNextPatient}
              style={{
                marginTop: 12,
                border: 0,
                background: 'transparent',
                color: '#514387',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Book next patient
            </button>
          </div>
        </CPBPOMContainer>
      )}

      <PublicAppFooter />
    </>
  )
}
